/** Objectives — edit form (RHF + zod + shadcn Form via Refine useForm). */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  updateObjectiveSchema,
  ENTITY_STATUS,
  type Objective,
  type UpdateObjectiveInput,
} from "@aos/shared";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { PageHeader, LoadingState, ErrorState } from "../projects/_shared";
import {
  FormGrid,
  MarkdownField,
  SelectField,
  TextField,
} from "../projects/_form";

/**
 * ObjectiveEdit (D6) — edit form backed by `updateObjectiveSchema`. Loads the
 * current record via Refine useForm and PATCHes /api/objectives/:id, mirroring
 * the create form's layout (markdown description, Select status).
 */
export function ObjectiveEdit() {
  const form = useForm<Objective, any, UpdateObjectiveInput>({
    resolver: zodResolver(updateObjectiveSchema),
    refineCoreProps: { resource: "objectives", action: "edit" },
  });

  const {
    refineCore: { formLoading, query },
    saveButtonProps,
    control,
  } = form;

  if (query?.isLoading) {
    return <LoadingState label="Loading objective…" />;
  }
  if (query?.isError) {
    return <ErrorState error={query.error} />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Edit Objective"
        description="Update the outcome, its period, status, and owner."
      />
      <Form {...form}>
        <form onSubmit={saveButtonProps.onClick}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Objective details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TextField control={control} name="title" label="Title" />
              <MarkdownField
                control={control}
                name="description"
                label="Description"
                placeholder="What outcome are we steering toward, and why?"
              />
              <FormGrid>
                <TextField
                  control={control}
                  name="period"
                  label="Period"
                  placeholder="2026-Q3"
                />
                <SelectField
                  control={control}
                  name="status"
                  label="Status"
                  options={ENTITY_STATUS}
                />
                <TextField
                  control={control}
                  name="owner_user_id"
                  label="Owner user ID"
                  placeholder="user_…"
                />
              </FormGrid>
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" disabled={formLoading}>
                {formLoading ? "Saving…" : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}

export default ObjectiveEdit;
